// frontend/src/config/appPages.js
// Single source of truth for all navigable pages in the app.
// Used by: the command palette (hooks/useCommandPalette.js), the reports hub
// (pages/reports/reportsCatalogue.js, ReportsHub via PageIcon) and the
// sidebar's top-level links (components/layout/Sidebar.jsx via PageIcon).
// When adding or removing a route in tabs/routeRegistry.jsx, update this file
// too.
//
// This file owns which glyph represents a page, everywhere the page is listed:
// anything that needs a page's glyph draws <PageIcon> rather than choosing one.

import { createElement } from 'react';
import { CalendarCheck, ChartBar, ChartLine, ClipboardText, Cube, CurrencyCircleDollar, GridFour, House, Lightning, MapPin, Package, Prohibit, Receipt, ShieldCheck, ShoppingCart, Storefront, Tag, Target, Truck, UserCircle, Warning } from '../icons';

// Categories for grouping in the command palette
export const CATEGORIES = {
  NAVIGATION: 'navigation',
  PURCHASE: 'purchase',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  TOOLS: 'tools',
  ACCOUNT: 'account',
  ACTIONS: 'actions',
};

// All navigable pages with search metadata. A page's name is its tab title
// (tabs/routeRegistry.jsx titleKey), so the palette never names a page twice.
// - descriptionKey: i18n key for short description (shown in command palette)
// - keywords: additional search terms (English, not translated — for fuzzy matching)
// - icon: glyph from src/icons.js — draw it with <PageIcon>, never re-pick
// - category: one of CATEGORIES
// - needsForecast: offered only once the store has a forecast
//   (`utils/syncGate.forecastsMissing`)
const APP_PAGES = [
  // --- Navigation ---
  {
    path: '/',
    descriptionKey: 'commandPalette.descriptions.home',
    keywords: ['dashboard', 'kpi', 'sales', 'overview', 'home', 'stockout risk', 'inventory value', 'projected stock', 'outstanding po', 'metrics', 'month to date', 'main page', 'landing', 'snapshot', 'summary', 'start'],
    icon: House,
    category: CATEGORIES.NAVIGATION,
  },
  {
    path: '/forecast',
    descriptionKey: 'commandPalette.descriptions.forecast',
    keywords: ['demand', 'forecast', 'planning', 'override', 'adjust', 'consensus', 'sales history', 'timeline', 'promoted only', 'import forecast', 'statistical forecast', 'manexys forecast', 'demand planning', 'sales forecast', 'forecast edit', 'forecast adjustment', 'prediction', 'demand plan', 'future demand', 'projected demand'],
    icon: ChartLine,
    category: CATEGORIES.NAVIGATION,
    needsForecast: true,
  },
  {
    path: '/promotions',
    descriptionKey: 'commandPalette.descriptions.promotions',
    keywords: ['promotions', 'campaign', 'discount', 'uplift', 'sales event', 'lift', 'markdown', 'scheduled', 'draft promo', 'sale', 'price cut', 'pre post', 'deal', 'offer', 'markdown event', 'price promotion', 'promo', 'special offer', 'limited offer'],
    icon: Tag,
    category: CATEGORIES.NAVIGATION,
  },
  {
    path: '/inventory',
    descriptionKey: 'commandPalette.descriptions.inventory',
    keywords: ['stock', 'inventory', 'on hand', 'warehouse', 'safety stock', 'committed', 'in transit', 'lead time', 'moq', 'abc', 'xyz', 'total stock', 'cost price', 'sell price', 'projected balance', 'stock levels', 'stockholding', 'current stock', 'on-hand', 'in stock', 'stock on hand', 'available stock', 'quantity on hand', 'qoh'],
    icon: Package,
    category: CATEGORIES.NAVIGATION,
  },
  {
    path: '/customer-orders',
    descriptionKey: 'commandPalette.descriptions.customerOrders',
    keywords: ['orders', 'sales', 'history', 'customer'],
    icon: ClipboardText,
    category: CATEGORIES.NAVIGATION,
    staffOnly: true,
  },

  // --- Purchase ---
  {
    path: '/purchase-recommendations',
    descriptionKey: 'commandPalette.descriptions.createPurchaseOrders',
    keywords: ['create', 'reorder', 'purchase', 'recommendations', 'replenishment', 'buy', 'po recommendations', 'suggested orders', 'emergency', 'today', 'planned', 'planning horizon', 'actionable today', 'recalculate', 'suggested purchase', 'reorder suggestion', 'replenish', 'restock', 'stock order', 'buying advice', 'auto reorder', 'order suggestions', 'replenishment plan'],
    icon: ShoppingCart,
    category: CATEGORIES.PURCHASE,
  },
  {
    path: '/purchase-orders',
    descriptionKey: 'commandPalette.descriptions.managePurchaseOrders',
    keywords: ['po', 'manage', 'purchase orders', 'receive', 'goods receipt', 'delivery', 'pdf', 'draft', 'issue po', 'outstanding', 'expected delivery date', 'need by', 'partially received', 'grn', 'receive goods', 'order to supplier', 'buy order', 'vendor order', 'procurement', 'supplier order', 'incoming stock', 'po list', 'orders to vendor'],
    icon: Receipt,
    category: CATEGORIES.PURCHASE,
  },

  // --- Reports ---
  {
    path: '/reports',
    descriptionKey: 'commandPalette.descriptions.reports',
    keywords: ['reports', 'analytics', 'hub', 'kpi', 'insights', 'analysis', 'dashboards', 'data', 'business reports', 'reporting'],
    icon: ChartBar,
    category: CATEGORIES.REPORTS,
  },
  {
    path: '/reports/sales-forecast-accuracy',
    descriptionKey: 'commandPalette.descriptions.salesForecastAccuracy',
    keywords: ['sales forecast accuracy', 'accuracy', 'forecast error', 'forecast reliability', 'forecast quality', 'mape', 'mae', 'bias', 'forecast method', 'low accuracy', 'high accuracy', 'over forecast', 'under forecast', 'overrides', 'forecast performance', 'prediction accuracy', 'model accuracy', 'demand accuracy', 'forecast precision', 'order progress', 'sales vs forecast', 'actual vs plan', 'variance', 'actual vs forecast', 'sales tracking', 'forecast comparison', 'forecast tracking', 'out of stock days'],
    icon: Target,
    category: CATEGORIES.REPORTS,
  },
  {
    path: '/reports/stockout',
    descriptionKey: 'commandPalette.descriptions.stockout',
    keywords: ['stockout', 'out of stock', 'risk', 'shortage', 'reorder point', 'days of cover', 'at risk', 'stockout date', 'needs reorder', 'stock risk', 'oos', 'running low', 'low stock', 'low inventory', 'stockout alert', 'run out', 'empty shelf', 'stock shortage', 'inventory risk'],
    icon: Warning,
    category: CATEGORIES.REPORTS,
  },
  {
    path: '/reports/excess-stock',
    descriptionKey: 'commandPalette.descriptions.excessStock',
    keywords: ['overstock', 'excess', 'capital', 'overstocked', 'surplus', 'projected stock', 'slow moving', 'tied capital', 'clearance', 'excess value', 'idle stock', 'excess inventory', 'excess products', 'excess items', 'overstocked items', 'surplus stock', 'surplus inventory', 'slow movers', 'dead money', 'overstock risk', 'too much stock', 'overage'],
    icon: Package,
    category: CATEGORIES.REPORTS,
  },
  {
    path: '/reports/classification',
    descriptionKey: 'commandPalette.descriptions.classification',
    keywords: ['abc', 'xyz', 'classification', 'pareto', 'revenue ranking', 'demand variability', 'revenue share', 'top sellers', 'stable demand', 'erratic demand', 'segmentation', 'item ranking', 'product classification', 'strategic items', 'item segmentation', 'product segmentation', 'abc analysis', 'xyz analysis', 'best sellers', 'key products'],
    icon: GridFour,
    category: CATEGORIES.REPORTS,
  },
  {
    path: '/reports/gmroi',
    descriptionKey: 'commandPalette.descriptions.gmroi',
    keywords: ['gmroi', 'margin', 'roi', 'profitability', 'inventory investment', 'return', 'gross margin', 'cogs', 'profit', 'net sales', 'inventory profitability', 'margin return', 'return on stock', 'inventory roi', 'stock roi', 'gross margin roi', 'inventory margin', 'product profitability'],
    icon: CurrencyCircleDollar,
    category: CATEGORIES.REPORTS,
  },
  {
    path: '/reports/dead-stock',
    descriptionKey: 'commandPalette.descriptions.deadStock',
    keywords: ['dead stock', 'inventory aging', 'watch', 'at risk', 'slow moving', 'carrying cost', 'clearance', 'obsolete', 'idle stock', 'no sales', 'never sold', 'npi', 'dead inventory', 'dead items', 'dead products', 'zombie stock', 'stale inventory', 'stale stock', 'aged stock', 'aging inventory', 'aged inventory', 'obsolete items', 'obsolete products', 'old stock', 'old inventory', 'unsold stock', 'unsold items', 'unsold products', 'non-moving', 'non moving', 'no sales items', 'stagnant inventory', 'stagnant stock'],
    icon: Prohibit,
    category: CATEGORIES.REPORTS,
  },

  // --- Settings ---
  {
    path: '/item-setup',
    descriptionKey: 'commandPalette.descriptions.items',
    keywords: ['items', 'products', 'pricing', 'sku', 'master data', 'catalog', 'cost price', 'sell price', 'compare at', 'npi', 'brand', 'product category', 'service level', 'variants', 'active items', 'item master', 'product master', 'sku list', 'product catalog', 'merchandise', 'stock keeping unit', 'products list', 'items list', 'product list', 'goods', 'article', 'articles'],
    icon: Cube,
    category: CATEGORIES.SETTINGS,
  },
  {
    path: '/supplier-setup',
    descriptionKey: 'commandPalette.descriptions.suppliers',
    keywords: ['suppliers', 'vendors', 'lead time', 'contacts', 'currency', 'review frequency', 'order day', 'delivers weekends', 'supplier address', 'exchange rate', 'vendor master', 'supplier master', 'providers', 'trading partners', 'manufacturers', 'supplier list', 'vendor list', 'partners'],
    icon: Truck,
    category: CATEGORIES.SETTINGS,
  },
  {
    path: '/locations-setup',
    descriptionKey: 'commandPalette.descriptions.locations',
    keywords: ['locations', 'warehouses', 'calendars', 'fulfillment', '3pl', 'working days', 'warehouse', 'store location', 'address', 'shipbob', 'printful', 'delivery rule', 'sites', 'stores', 'fulfillment center', 'fc', 'dc', 'distribution center', 'branch', 'stock location', 'facility', 'depot', 'shop location'],
    icon: MapPin,
    category: CATEGORIES.SETTINGS,
  },

  // --- Tools ---
  {
    path: '/forecast-generator',
    descriptionKey: 'commandPalette.descriptions.forecastGenerator',
    keywords: ['generate forecast', 'run forecast', 'demand engine', 'models'],
    icon: Lightning,
    category: CATEGORIES.TOOLS,
    staffOnly: true,
  },
  {
    path: '/safety-stock-generator',
    descriptionKey: 'commandPalette.descriptions.safetyStockGenerator',
    keywords: ['safety stock', 'abc', 'xyz', 'classification', 'service level'],
    icon: ShieldCheck,
    category: CATEGORIES.TOOLS,
    staffOnly: true,
  },
  {
    path: '/data-health',
    descriptionKey: 'commandPalette.descriptions.dataHealth',
    keywords: ['data quality', 'health score', 'missing data', 'issues', 'fix', 'missing supplier', 'anomalies', 'tasks', 'reconcile cost', 'fx drift', 'negative inventory', 'price below cost', 'product category', 'uncategorized', 'health check', 'data audit', 'data issues', 'data problems', 'quality check', 'diagnostics', 'master data health', 'data cleanup', 'data errors', 'data fixes'],
    icon: CalendarCheck,
    category: CATEGORIES.TOOLS,
  },
  {
    path: '/search-analytics',
    descriptionKey: 'commandPalette.descriptions.searchAnalytics',
    keywords: ['search analytics', 'search insights', 'search log', 'palette analytics', 'zero results', 'top queries', 'what users search', 'command palette usage'],
    icon: ChartBar,
    category: CATEGORIES.TOOLS,
    staffOnly: true,
  },
  {
    path: '/store-activity',
    descriptionKey: 'commandPalette.descriptions.storeActivity',
    keywords: ['store activity', 'merchant activity', 'user behavior', 'page views', 'what they did', 'churn', 'usage', 'timeline', 'per store analytics', 'customer activity', 'engagement'],
    icon: ChartBar,
    category: CATEGORIES.TOOLS,
    staffOnly: true,
  },
  {
    path: '/store-data-health',
    descriptionKey: 'commandPalette.descriptions.storeDataHealth',
    keywords: ['store data health', 'merchant data health', 'data issues per store', 'support diagnostics', 'any store health', 'issue counts', 'admin data health'],
    icon: ChartBar,
    category: CATEGORIES.TOOLS,
    staffOnly: true,
  },

  // --- Account ---
  {
    path: '/user-profile',
    descriptionKey: 'commandPalette.descriptions.userProfile',
    keywords: ['profile', 'avatar', 'password', 'theme', 'preferences', 'language', 'timezone', 'date format', 'chart colors', 'my account', 'email notifications', 'appearance', 'dark mode', 'personal settings', 'user settings', 'account settings', 'my settings', 'me', 'user profile', 'user account'],
    icon: UserCircle,
    category: CATEGORIES.ACCOUNT,
  },
  {
    path: '/store-settings',
    descriptionKey: 'commandPalette.descriptions.storeSettings',
    keywords: ['store', 'settings', 'configuration', 'shop', 'planning horizon', 'po pdf', 'logo', 'organization', 'company', 'regional', 'formatting', 'currency', 'company settings', 'tenant settings', 'shop settings', 'admin settings', 'global settings', 'store config', 'system settings', 'business settings', 'organization settings'],
    icon: Storefront,
    category: CATEGORIES.ACCOUNT,
  },
];

// Quick actions — navigate to a page with intent
export const APP_ACTIONS = [
  {
    id: 'action-add-supplier',
    labelKey: 'commandPalette.actions.createSupplier',
    keywords: ['add supplier', 'new supplier', 'create supplier'],
    icon: Truck,
    path: '/supplier-setup',
    category: CATEGORIES.ACTIONS,
  },
  {
    id: 'action-add-item',
    labelKey: 'commandPalette.actions.addItem',
    keywords: ['add item', 'new item', 'create item', 'add product'],
    icon: Cube,
    path: '/item-setup',
    category: CATEGORIES.ACTIONS,
  },
  {
    id: 'action-add-location',
    labelKey: 'commandPalette.actions.addLocation',
    keywords: ['add location', 'new location', 'create warehouse'],
    icon: MapPin,
    path: '/locations-setup',
    category: CATEGORIES.ACTIONS,
  },
  {
    id: 'action-run-forecast',
    labelKey: 'commandPalette.actions.runForecast',
    keywords: ['run forecast', 'generate forecast', 'calculate demand'],
    icon: Lightning,
    path: '/forecast-generator',
    category: CATEGORIES.ACTIONS,
    staffOnly: true,
  },
  {
    id: 'action-run-classification',
    labelKey: 'commandPalette.actions.runClassification',
    keywords: ['run classification', 'abc xyz', 'calculate safety stock'],
    icon: ShieldCheck,
    path: '/safety-stock-generator',
    category: CATEGORIES.ACTIONS,
    staffOnly: true,
  },
  {
    id: 'action-change-password',
    labelKey: 'commandPalette.actions.changePassword',
    keywords: ['change password', 'reset password', 'security'],
    icon: UserCircle,
    path: '/user-profile',
    category: CATEGORIES.ACTIONS,
  },
  {
    id: 'action-change-theme',
    labelKey: 'commandPalette.actions.changeTheme',
    keywords: ['change theme', 'dark mode', 'light mode', 'appearance'],
    icon: UserCircle,
    path: '/user-profile',
    category: CATEGORIES.ACTIONS,
  },
];

/**
 * The glyph representing a page, drawn — for anywhere the page is listed.
 * Every prop but `path` goes to the glyph (`size`, `className`, `weight`).
 *
 * The element type is an APP_PAGES entry, a module-level component, so it is
 * the same object on every render and the glyph never remounts.
 *
 * Raises on an unregistered route rather than rendering nothing, so a surface
 * never silently loses its icon.
 */
export function PageIcon({ path, ...props }) {
  const page = APP_PAGES.find((entry) => entry.path === path);
  if (!page) {
    throw new Error(`PageIcon: no page registered for "${path}"`);
  }
  return createElement(page.icon, props);
}

export default APP_PAGES;
